import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Pencil, ChevronDown, ChevronUp, Zap, Clock, AlertTriangle, Mail, Bell, ClipboardList, ToggleLeft, ToggleRight, History } from 'lucide-react';
import clsx from 'clsx';
import { apiFetch } from '../shared/hooks/useApi.js';
import { Card } from '../shared/components/Card.js';
import { Button } from '../shared/components/Button.js';
import { Modal } from '../shared/components/Modal.js';
import { useToast } from '../shared/components/Toast.js';

// ─── Types ──────────────────────────────────────────────────────

interface AutomationRule {
  id: string;
  naam: string;
  beschrijving: string | null;
  trigger: string;
  action: string;
  triggerConfig: any;
  actionConfig: any;
  active: boolean;
  volgorde: number;
  _count: { executions: number };
  createdAt: string;
}

interface WorkflowExecution {
  id: string;
  ruleId: string;
  dossierId: string | null;
  status: string;
  resultaat: string | null;
  foutmelding: string | null;
  createdAt: string;
}

// ─── Consts ─────────────────────────────────────────────────────

const TRIGGERS: { value: string; label: string; description: string; icon: React.ReactNode }[] = [
  { value: 'TRAJECTSTAP_GESTART', label: 'Trajectstap gestart', description: 'Wanneer een trajectstap actief wordt', icon: <Zap size={16} className="text-green-600" /> },
  { value: 'TRAJECTSTAP_VOLTOOID', label: 'Trajectstap voltooid', description: 'Wanneer een trajectstap afgerond wordt', icon: <Zap size={16} className="text-blue-600" /> },
  { value: 'HERINNERING_VOOR_DATUM', label: 'Herinnering voor datum', description: 'X dagen voor geplande datum', icon: <Clock size={16} className="text-amber-600" /> },
  { value: 'KLANT_GEEN_REACTIE', label: 'Klant geen reactie', description: 'Escalatie na X dagen zonder reactie', icon: <AlertTriangle size={16} className="text-red-600" /> },
  { value: 'DATABUS_EVENT', label: 'Databus event', description: 'Bij ontvangst van een WMS/extern event', icon: <Zap size={16} className="text-purple-600" /> },
];

const ACTIONS: { value: string; label: string; icon: React.ReactNode }[] = [
  { value: 'BERICHT_NAAR_KLANT', label: 'Bericht naar klant', icon: <Mail size={16} className="text-blue-600" /> },
  { value: 'NOTIFICATIE_MEDEWERKER', label: 'Notificatie medewerker', icon: <Bell size={16} className="text-amber-600" /> },
  { value: 'TAAK_AANMAKEN', label: 'Taak aanmaken', icon: <ClipboardList size={16} className="text-green-600" /> },
];

// ─── Hooks ──────────────────────────────────────────────────────

function useAutomationRules() {
  return useQuery<AutomationRule[]>({
    queryKey: ['automation', 'rules'],
    queryFn: () => apiFetch('/api/automation/rules'),
  });
}

function useRuleLog(ruleId: string | null) {
  return useQuery<WorkflowExecution[]>({
    queryKey: ['automation', 'log', ruleId],
    queryFn: () => apiFetch(`/api/automation/rules/${ruleId}/log`),
    enabled: !!ruleId,
  });
}

// ─── Component ──────────────────────────────────────────────────

export function AutomatiseringTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: rules, isLoading } = useAutomationRules();
  const [editRule, setEditRule] = useState<AutomationRule | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [logRuleId, setLogRuleId] = useState<string | null>(null);
  const { data: logEntries } = useRuleLog(logRuleId);

  const toggleMutation = useMutation({
    mutationFn: (rule: AutomationRule) =>
      apiFetch(`/api/automation/rules/${rule.id}`, { method: 'PUT', body: JSON.stringify({ active: !rule.active }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['automation'] }); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/automation/rules/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['automation'] }); toast('success', 'Regel verwijderd'); },
  });

  const grouped = TRIGGERS.map(t => ({
    ...t,
    rules: (rules || []).filter(r => r.trigger === t.value),
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Automatisering</h2>
          <p className="text-sm text-gray-500">Configureer automatische berichten, herinneringen en escalaties.</p>
        </div>
        <Button onClick={() => { setEditRule(null); setShowNew(true); }}>
          <Plus size={16} className="mr-1" /> Nieuwe regel
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(group => (
            <div key={group.value}>
              <div className="flex items-center gap-2 mb-2">
                {group.icon}
                <h3 className="text-sm font-semibold text-gray-700">{group.label}</h3>
                <span className="text-xs text-gray-400">{group.description}</span>
                {group.rules.length > 0 && (
                  <span className="text-xs bg-gold/10 text-gold px-2 py-0.5 rounded-full">{group.rules.length}</span>
                )}
              </div>

              {group.rules.length === 0 ? (
                <p className="text-xs text-gray-400 ml-6 mb-3">Geen regels geconfigureerd</p>
              ) : (
                <div className="space-y-2 mb-3">
                  {group.rules.map(rule => (
                    <RuleCard
                      key={rule.id}
                      rule={rule}
                      onToggle={() => toggleMutation.mutate(rule)}
                      onEdit={() => { setEditRule(rule); setShowNew(true); }}
                      onDelete={() => { if (confirm(`Regel "${rule.naam}" verwijderen?`)) deleteMutation.mutate(rule.id); }}
                      onLog={() => setLogRuleId(logRuleId === rule.id ? null : rule.id)}
                      isLogOpen={logRuleId === rule.id}
                      logEntries={logRuleId === rule.id ? logEntries : undefined}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <RuleModal
          rule={editRule}
          onClose={() => { setShowNew(false); setEditRule(null); }}
          onSaved={() => { setShowNew(false); setEditRule(null); qc.invalidateQueries({ queryKey: ['automation'] }); }}
        />
      )}
    </div>
  );
}

// ─── Rule Card ──────────────────────────────────────────────────

function RuleCard({ rule, onToggle, onEdit, onDelete, onLog, isLogOpen, logEntries }: {
  rule: AutomationRule;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onLog: () => void;
  isLogOpen: boolean;
  logEntries?: WorkflowExecution[];
}) {
  const actionInfo = ACTIONS.find(a => a.value === rule.action);

  return (
    <Card className="!p-0">
      <div className="flex items-center gap-3 p-3">
        <button onClick={onToggle} className="flex-shrink-0" title={rule.active ? 'Deactiveren' : 'Activeren'}>
          {rule.active
            ? <ToggleRight size={24} className="text-green-500" />
            : <ToggleLeft size={24} className="text-gray-300" />
          }
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={clsx('text-sm font-medium', rule.active ? 'text-gray-900' : 'text-gray-400')}>
              {rule.naam}
            </span>
            {actionInfo && (
              <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                {actionInfo.icon} {actionInfo.label}
              </span>
            )}
          </div>
          {rule.beschrijving && (
            <p className="text-xs text-gray-400 truncate">{rule.beschrijving}</p>
          )}
          <TriggerConfigSummary rule={rule} />
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="text-xs text-gray-400 mr-2">{rule._count.executions}x</span>
          <button onClick={onLog} className="p-1.5 rounded hover:bg-gray-100" title="Logboek">
            <History size={14} className="text-gray-400" />
          </button>
          <button onClick={onEdit} className="p-1.5 rounded hover:bg-gray-100" title="Bewerken">
            <Pencil size={14} className="text-gray-400" />
          </button>
          <button onClick={onDelete} className="p-1.5 rounded hover:bg-red-50" title="Verwijderen">
            <Trash2 size={14} className="text-gray-400 hover:text-red-500" />
          </button>
        </div>
      </div>

      {isLogOpen && logEntries && (
        <div className="border-t border-gray-100 bg-gray-50 p-3 max-h-48 overflow-y-auto">
          {logEntries.length === 0 ? (
            <p className="text-xs text-gray-400">Nog geen uitvoeringen</p>
          ) : (
            <div className="space-y-1">
              {logEntries.map(entry => (
                <div key={entry.id} className="flex items-center gap-2 text-xs">
                  <span className={clsx(
                    'w-2 h-2 rounded-full flex-shrink-0',
                    entry.status === 'uitgevoerd' ? 'bg-green-400' :
                    entry.status === 'mislukt' ? 'bg-red-400' : 'bg-gray-300'
                  )} />
                  <span className="text-gray-400 flex-shrink-0">
                    {new Date(entry.createdAt).toLocaleDateString('nl-BE')} {new Date(entry.createdAt).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span className="text-gray-600 truncate">{entry.resultaat || entry.foutmelding || entry.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ─── Trigger config summary ─────────────────────────────────────

function TriggerConfigSummary({ rule }: { rule: AutomationRule }) {
  const config = rule.triggerConfig || {};
  const parts: string[] = [];

  if (config.milestoneKey) parts.push(`stap: ${config.milestoneKey}`);
  if (config.dagenVoor) parts.push(`${config.dagenVoor} dagen vooraf`);
  if (config.dagenWachten) parts.push(`na ${config.dagenWachten} dagen`);
  if (config.eventType) parts.push(`event: ${config.eventType}`);

  const actionConfig = rule.actionConfig || {};
  if (actionConfig.kanaal && actionConfig.kanaal !== 'voorkeur') parts.push(`via ${actionConfig.kanaal}`);

  if (parts.length === 0) return null;

  return (
    <p className="text-[11px] text-gray-400 mt-0.5">{parts.join(' · ')}</p>
  );
}

// ─── Rule Modal ─────────────────────────────────────────────────

function RuleModal({ rule, onClose, onSaved }: {
  rule: AutomationRule | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const isEdit = !!rule;

  const [naam, setNaam] = useState(rule?.naam || '');
  const [beschrijving, setBeschrijving] = useState(rule?.beschrijving || '');
  const [trigger, setTrigger] = useState(rule?.trigger || 'TRAJECTSTAP_VOLTOOID');
  const [action, setAction] = useState(rule?.action || 'BERICHT_NAAR_KLANT');

  // Trigger config
  const tc = rule?.triggerConfig || {};
  const [milestoneKey, setMilestoneKey] = useState(tc.milestoneKey || '');
  const [dagenVoor, setDagenVoor] = useState(tc.dagenVoor || 3);
  const [dagenWachten, setDagenWachten] = useState(tc.dagenWachten || 5);
  const [eventType, setEventType] = useState(tc.eventType || '');

  // Action config
  const ac = rule?.actionConfig || {};
  const [kanaal, setKanaal] = useState(ac.kanaal || 'voorkeur');
  const [bericht, setBericht] = useState(ac.bericht || ac.content || '');
  const [onderwerp, setOnderwerp] = useState(ac.onderwerp || '');
  const [notifTitel, setNotifTitel] = useState(ac.titel || '');
  const [notifBericht, setNotifBericht] = useState(ac.bericht || '');
  const [taakTitel, setTaakTitel] = useState(ac.titel || '');
  const [prioriteit, setPrioriteit] = useState(ac.prioriteit || 'NORMAAL');

  const mutation = useMutation({
    mutationFn: (data: any) => {
      if (isEdit) {
        return apiFetch(`/api/automation/rules/${rule!.id}`, { method: 'PUT', body: JSON.stringify(data) });
      }
      return apiFetch('/api/automation/rules', { method: 'POST', body: JSON.stringify(data) });
    },
    onSuccess: () => {
      toast('success', isEdit ? 'Regel bijgewerkt' : 'Regel aangemaakt');
      onSaved();
    },
    onError: () => toast('error', 'Fout bij opslaan'),
  });

  const handleSave = () => {
    // Build trigger config
    const triggerConfig: any = {};
    if ((trigger === 'TRAJECTSTAP_GESTART' || trigger === 'TRAJECTSTAP_VOLTOOID') && milestoneKey) {
      triggerConfig.milestoneKey = milestoneKey;
    }
    if (trigger === 'HERINNERING_VOOR_DATUM') {
      triggerConfig.dagenVoor = dagenVoor;
      if (milestoneKey) triggerConfig.milestoneKey = milestoneKey;
    }
    if (trigger === 'KLANT_GEEN_REACTIE') {
      triggerConfig.dagenWachten = dagenWachten;
    }
    if (trigger === 'DATABUS_EVENT' && eventType) {
      triggerConfig.eventType = eventType;
    }

    // Build action config
    const actionConfig: any = {};
    if (action === 'BERICHT_NAAR_KLANT') {
      actionConfig.kanaal = kanaal;
      actionConfig.bericht = bericht;
      if (onderwerp) actionConfig.onderwerp = onderwerp;
    }
    if (action === 'NOTIFICATIE_MEDEWERKER') {
      actionConfig.titel = notifTitel;
      actionConfig.bericht = notifBericht;
    }
    if (action === 'TAAK_AANMAKEN') {
      actionConfig.titel = taakTitel;
      actionConfig.prioriteit = prioriteit;
    }

    mutation.mutate({ naam, beschrijving: beschrijving || null, trigger, action, triggerConfig, actionConfig });
  };

  return (
    <Modal open={true} title={isEdit ? 'Regel bewerken' : 'Nieuwe automatisering'} onClose={onClose} size="lg">
      <div className="space-y-4">
        {/* Naam */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Naam</label>
          <input value={naam} onChange={e => setNaam(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none"
            placeholder="Bijv. Bevestiging na opmeting" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Beschrijving (optioneel)</label>
          <input value={beschrijving} onChange={e => setBeschrijving(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none"
            placeholder="Korte beschrijving van deze regel" />
        </div>

        {/* Trigger */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Trigger</label>
          <select value={trigger} onChange={e => setTrigger(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none bg-white">
            {TRIGGERS.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        {/* Trigger-specifieke config */}
        {(trigger === 'TRAJECTSTAP_GESTART' || trigger === 'TRAJECTSTAP_VOLTOOID' || trigger === 'HERINNERING_VOOR_DATUM') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Trajectstap (leeg = alle stappen)
            </label>
            <select value={milestoneKey} onChange={e => setMilestoneKey(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none">
              <option value="">Alle trajectstappen</option>
              <option value="opmeting">Opmeting</option>
              <option value="levering">Levering</option>
              <option value="plaatsing">Plaatsing</option>
              <option value="oplevering">Oplevering</option>
              <option value="nazorg">Nazorg</option>
            </select>
          </div>
        )}

        {trigger === 'HERINNERING_VOOR_DATUM' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dagen vooraf</label>
            <input type="number" value={dagenVoor} onChange={e => setDagenVoor(Number(e.target.value))}
              min={1} max={30}
              className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
          </div>
        )}

        {trigger === 'KLANT_GEEN_REACTIE' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dagen wachten</label>
            <input type="number" value={dagenWachten} onChange={e => setDagenWachten(Number(e.target.value))}
              min={1} max={30}
              className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none" />
          </div>
        )}

        {trigger === 'DATABUS_EVENT' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event type</label>
            <select value={eventType} onChange={e => setEventType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none">
              <option value="">Alle events</option>
              <option value="GOEDEREN_ONTVANGEN">Goederen ontvangen</option>
              <option value="LEVERING_GEPLAND">Levering gepland</option>
              <option value="LEVERING_VOLTOOID">Levering voltooid</option>
              <option value="VORDERINGSSTAAT_INGEDIEND">Vorderingsstaat ingediend</option>
            </select>
          </div>
        )}

        <hr className="border-gray-200" />

        {/* Action */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Actie</label>
          <select value={action} onChange={e => setAction(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none bg-white">
            {ACTIONS.map(a => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
          </select>
        </div>

        {/* Action-specifieke config */}
        {action === 'BERICHT_NAAR_KLANT' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kanaal</label>
              <select value={kanaal} onChange={e => setKanaal(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none">
                <option value="voorkeur">Voorkeurskanaal klant</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="email">E-mail</option>
                <option value="sms">SMS</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Onderwerp (e-mail)</label>
              <input value={onderwerp} onChange={e => setOnderwerp(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none"
                placeholder="Update over uw dossier {dossiernummer}" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bericht</label>
              <textarea value={bericht} onChange={e => setBericht(e.target.value)} rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none resize-y"
                placeholder="Beste {klantnaam}, uw {stap} is afgerond..." />
              <p className="text-[11px] text-gray-400 mt-1">
                Variabelen: {'{klantnaam}'}, {'{referentie}'}, {'{dossiernummer}'}, {'{datum}'}, {'{adres}'}, {'{stap}'}, {'{status}'}
              </p>
            </div>
          </>
        )}

        {action === 'NOTIFICATIE_MEDEWERKER' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
              <input value={notifTitel} onChange={e => setNotifTitel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none"
                placeholder="Actie vereist" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bericht</label>
              <textarea value={notifBericht} onChange={e => setNotifBericht(e.target.value)} rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none resize-y"
                placeholder="Klant van dossier {referentie} heeft niet gereageerd..." />
            </div>
          </>
        )}

        {action === 'TAAK_AANMAKEN' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Taak titel</label>
              <input value={taakTitel} onChange={e => setTaakTitel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none"
                placeholder="Opvolging nodig voor {klantnaam}" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prioriteit</label>
              <select value={prioriteit} onChange={e => setPrioriteit(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gold/30 focus:border-gold outline-none">
                <option value="LAAG">Laag</option>
                <option value="NORMAAL">Normaal</option>
                <option value="HOOG">Hoog</option>
                <option value="URGENT">Urgent</option>
              </select>
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose}>Annuleren</Button>
          <Button onClick={handleSave} disabled={mutation.isPending || !naam}>
            {mutation.isPending ? 'Opslaan...' : isEdit ? 'Bijwerken' : 'Aanmaken'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
